import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";

const ABILITY = [
  { v: "complete_beginner", l: "Complete beginner", d: "First time or almost — soft board only" },
  { v: "beginner", l: "Beginner", d: "Getting up on foamies, learning to catch green waves" },
  { v: "improving_beginner", l: "Improving beginner", d: "Catching unbroken waves and starting turns" },
  { v: "lower_intermediate", l: "Lower intermediate", d: "Confident on green waves, exploring shortboards" },
  { v: "intermediate", l: "Intermediate", d: "Reading conditions, working on manoeuvres" },
];
const BOARD = [
  { v: "foam", l: "Foam board" },
  { v: "longboard", l: "Longboard" },
  { v: "mid_length", l: "Mid-length" },
  { v: "shortboard", l: "Shortboard" },
  { v: "bodyboard", l: "Bodyboard" },
  { v: "not_sure", l: "Not sure yet" },
];
const WAVE = [
  { v: "very_small", l: "Very small" },
  { v: "knee_waist", l: "Knee to waist" },
  { v: "waist_chest", l: "Waist to chest" },
  { v: "chest_head", l: "Chest to head" },
  { v: "not_sure", l: "Not sure" },
];
const CROWD = [
  { v: "quiet", l: "I prefer quiet surf" },
  { v: "moderate_ok", l: "Moderate crowds are fine" },
  { v: "any", l: "Crowd level is not important" },
];

function Chip({ selected, children, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-2xl border px-4 py-3 transition-colors ${
        selected
          ? "border-teal-800 bg-teal-800 text-white shadow-[0_6px_14px_rgba(0,78,89,0.15)]"
          : "border-border bg-white hover:border-teal-800/50"
      }`}
    >
      {children}
    </button>
  );
}

export default function Onboarding() {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [p, setP] = useState(() => ({
    ability: user?.profile?.ability || "beginner",
    board_type: user?.profile?.board_type || "foam",
    preferred_wave_size: user?.profile?.preferred_wave_size || "knee_waist",
    max_comfortable_wave: user?.profile?.max_comfortable_wave || "waist_chest",
    crowd_preference: user?.profile?.crowd_preference || "moderate_ok",
    max_travel_minutes: user?.profile?.max_travel_minutes || 60,
    travel_modes: user?.profile?.travel_modes || ["car"],
    owns_board: user?.profile?.owns_board ?? false,
    needs_board_hire: user?.profile?.needs_board_hire ?? true,
    owns_wetsuit: user?.profile?.owns_wetsuit ?? false,
    needs_wetsuit_hire: user?.profile?.needs_wetsuit_hire ?? true,
    may_need_lesson: user?.profile?.may_need_lesson ?? true,
    water_temp_preference: user?.profile?.water_temp_preference || "moderate",
    safety_confidence: user?.profile?.safety_confidence || "beginner_only",
    home_location: user?.profile?.home_location || "",
    home_lat: user?.profile?.home_lat ?? null,
    home_lon: user?.profile?.home_lon ?? null,
    onboarded: true,
  }));

  const total = 5;
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const finish = async () => {
    setBusy(true);
    const r = await updateProfile(p);
    setBusy(false);
    if (r.ok) {
      toast.success("Profile saved");
      navigate("/dashboard");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 py-10 md:py-16" data-testid="onboarding-page">
      <div className="flex items-center gap-2 mb-6">
        {Array.from({ length: total }).map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full ${i <= step ? "bg-teal-800" : "bg-sand-200"}`}
            data-testid={`step-indicator-${i}`}
          />
        ))}
      </div>

      {step === 0 && (
        <div>
          <div className="overline">Step 1 of 5</div>
          <h1 className="font-display text-3xl md:text-4xl text-teal-900 mt-2">What's your ability?</h1>
          <p className="text-sm text-muted-foreground mt-2">
            We use this to filter out surf that is too big or too powerful.
          </p>
          <div className="mt-6 grid gap-3">
            {ABILITY.map((a) => (
              <Chip
                key={a.v}
                selected={p.ability === a.v}
                onClick={() => set("ability", a.v)}
                testId={`onboard-ability-${a.v}`}
              >
                <div className="font-medium">{a.l}</div>
                <div className="text-xs opacity-80 mt-1">{a.d}</div>
              </Chip>
            ))}
          </div>
        </div>
      )}

      {step === 1 && (
        <div>
          <div className="overline">Step 2 of 5</div>
          <h1 className="font-display text-3xl md:text-4xl text-teal-900 mt-2">What board are you riding?</h1>
          <div className="mt-6 grid grid-cols-2 gap-3">
            {BOARD.map((b) => (
              <Chip key={b.v} selected={p.board_type === b.v} onClick={() => set("board_type", b.v)} testId={`onboard-board-${b.v}`}>
                {b.l}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <div className="overline">Step 3 of 5</div>
          <h1 className="font-display text-3xl md:text-4xl text-teal-900 mt-2">Preferred wave size</h1>
          <p className="text-sm text-muted-foreground mt-2">What size do you enjoy?</p>
          <div className="mt-6 grid grid-cols-2 gap-3">
            {WAVE.map((w) => (
              <Chip key={w.v} selected={p.preferred_wave_size === w.v} onClick={() => set("preferred_wave_size", w.v)} testId={`onboard-wave-${w.v}`}>
                {w.l}
              </Chip>
            ))}
          </div>
          <h2 className="mt-8 font-display text-xl text-teal-900">Largest you'll comfortably enter?</h2>
          <div className="mt-3 grid grid-cols-2 gap-3">
            {WAVE.map((w) => (
              <Chip key={w.v} selected={p.max_comfortable_wave === w.v} onClick={() => set("max_comfortable_wave", w.v)} testId={`onboard-max-${w.v}`}>
                {w.l}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <div className="overline">Step 4 of 5</div>
          <h1 className="font-display text-3xl md:text-4xl text-teal-900 mt-2">Crowds & travel</h1>
          <div className="mt-6 grid gap-3">
            {CROWD.map((c) => (
              <Chip key={c.v} selected={p.crowd_preference === c.v} onClick={() => set("crowd_preference", c.v)} testId={`onboard-crowd-${c.v}`}>
                {c.l}
              </Chip>
            ))}
          </div>
          <h2 className="mt-8 font-display text-xl text-teal-900">Max travel time</h2>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {[30, 60, 90, 120, 180, 240].map((m) => (
              <Chip key={m} selected={p.max_travel_minutes === m} onClick={() => set("max_travel_minutes", m)} testId={`onboard-travel-${m}`}>
                {m} min
              </Chip>
            ))}
          </div>
        </div>
      )}

      {step === 4 && (
        <div>
          <div className="overline">Step 5 of 5</div>
          <h1 className="font-display text-3xl md:text-4xl text-teal-900 mt-2">Equipment & starting point</h1>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <Chip selected={p.owns_board} onClick={() => set("owns_board", !p.owns_board)} testId="onboard-owns-board">
              {p.owns_board ? "✓ " : ""}I own a board
            </Chip>
            <Chip selected={p.owns_wetsuit} onClick={() => set("owns_wetsuit", !p.owns_wetsuit)} testId="onboard-owns-wetsuit">
              {p.owns_wetsuit ? "✓ " : ""}I own a wetsuit
            </Chip>
            <Chip selected={p.needs_board_hire} onClick={() => set("needs_board_hire", !p.needs_board_hire)} testId="onboard-needs-board">
              {p.needs_board_hire ? "✓ " : ""}I may need board hire
            </Chip>
            <Chip selected={p.may_need_lesson} onClick={() => set("may_need_lesson", !p.may_need_lesson)} testId="onboard-needs-lesson">
              {p.may_need_lesson ? "✓ " : ""}I may want a lesson
            </Chip>
          </div>
          <div className="mt-8">
            <label className="text-xs font-semibold text-muted-foreground" htmlFor="home">Home town (optional)</label>
            <Input
              id="home"
              data-testid="onboard-home"
              placeholder="e.g. Newquay, Cornwall"
              value={p.home_location || ""}
              onChange={(e) => set("home_location", e.target.value)}
              className="h-11 mt-1"
            />
          </div>
        </div>
      )}

      <div className="mt-10 flex items-center justify-between">
        <button
          type="button"
          data-testid="onboard-back"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="text-sm text-muted-foreground disabled:opacity-40"
        >
          Back
        </button>
        {step < total - 1 ? (
          <button
            type="button"
            data-testid="onboard-next"
            onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
            className="h-11 px-6 rounded-full bg-teal-800 text-white text-sm font-medium hover:bg-teal-900"
          >
            Continue
          </button>
        ) : (
          <button
            type="button"
            data-testid="onboard-finish"
            onClick={finish}
            disabled={busy}
            className="h-11 px-6 rounded-full bg-terracotta-500 text-white text-sm font-medium hover:bg-terracotta-600 disabled:opacity-60"
          >
            {busy ? "Saving…" : "Finish onboarding"}
          </button>
        )}
      </div>
    </div>
  );
}
